import { useContext, useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import Physician from "../../Physician/Models/Physician";
import { AuthContext } from "../../Auth/AuthProvider";
import { abpnClient } from "../../../Services/AbpnClient";
import Examination, { KnownCertifications, KnownExaminationDefinitions } from "../Models/Examination";
import ApplicantInformation from "../../Shared/Components/ApplicantInformation";
import PaymentCard from "./PaymentCard";
import ApplicationModel, { CandidateStatus } from "../Models/ApplicationModel"
import { MedicalLicenseInformation } from "./MedicalLicenseInformation";
import AttestationStatement from "./AttestationStatement";
import TestingOptions from "./TestingOptions";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { TrainingRequirements } from "./TrainingRequirements";
import SubspecialtyTraining from "../../Physician/Models/SubspecialtyTraining";
import AbpnSpinner from "../../Shared/Components/AbpnSpinner";
import ApplicationReceived from "./ApplicationReceived";
import Modules from "./Modules";
import CnpSection from "./CnpSection";
import { Address } from "../../Physician/Models/Address";
import ActionResultMessage from "../../Shared/Components/ActionResultMessage";
import { ApplicationPaths } from "../../../Services/Auth/AuthConstants";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { CCAssessmentType } from "../../Physician/Models/CCEnrollment";
import { configService } from "../../../Services/Config/ConfigService";
import InvoicePaymentCard from "./InvoicePaymentCard";
import { booleanify } from "../../Shared/Utility/Booleanify";

type TParams = { examinationId: string };

interface INavigation {
    navigateToPaySection: boolean,
    sectionsComplete: number,
    activeSections: Array<ApplicationSection>,
}

enum ApplicationSection {
    ApplicantInformation,
    MedicalLicenseInformation,
    Modules,
    CnpSection,
    TrainingRequirements,
    AttestationStatement,
    TestingOptions,
    Payment
}

function Application({ match }: RouteComponentProps<TParams>) {

    const [physician, setPhysician] = useState<Physician>(null);
    const [examination, setExamination] = useState<Examination>(null);
    const [application, setApplication] = useState<ApplicationModel>(null);
    const [attestationContent, setAttestationContent] = useState<string>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [displayPaymentSuccessful, setDisplayPaymentSuccessful] = useState<boolean>(false);
    const [useBC, setUseBC] = useState<boolean>(false);
    const [sectionNavigation, setSectionNav] = useState<INavigation>({navigateToPaySection: false, sectionsComplete: null, activeSections: null});

    const examinationDates = examination ? `Exam Dates: (${getDateWithOffset(examination.startDate).toLocaleDateString()} - ${getDateWithOffset(examination.endDate).toLocaleDateString()})` : null;
    const applicationRequiresTrainingSection = examination?.requiresFellowshipTraining && !application?.isTransferredIn && application?.candidateStatus != CandidateStatus.InviteSent;

    const applicationDeadlineDate = application?.extensionDate ?? examination?.applicationDeadlineDate;
    const exceptionText =  application?.hasDeadlineException ? "Exception" : "";
    const formattedApplicationDeadlineDate = applicationDeadlineDate ? `Application ${exceptionText} Deadline: ${getDateWithOffset(applicationDeadlineDate).toLocaleDateString()}` : null

    const authContext = useContext(AuthContext);
    const USCountryId = 1;

    const previousSectionComplete = (section: ApplicationSection) => {
        return (sectionNavigation.sectionsComplete === sectionNavigation.activeSections.indexOf(section));
    }

    const saveApplication = async (model: ApplicationModel, isNewApplication: boolean = false) => {
        setLoading(true);
        
        return abpnClient.post<ApplicationModel>("/api/application/update", model, authContext).then(result => {
            setApplication(result);
            
            if (!isNewApplication) {
                var updated = deepCopy(sectionNavigation);
                updated.sectionsComplete++;
                setSectionNav(updated);
            }
            setLoading(false);
        })
    };

    useEffect(() => {
        abpnClient.get<ApplicationModel>("/api/application?examinationId=" + match.params.examinationId, authContext).then(async existingApplication => {
            //If no application found create a new one
            if (existingApplication == null) {
                var applicationDto = new ApplicationModel();
                applicationDto.examinationId = parseInt(match.params.examinationId);

                await saveApplication(applicationDto, true);
            } else {
                setApplication(existingApplication);
            }

            abpnClient.get<Physician>("/api/physician", authContext)
                .then(physician => {
                    setPhysician(physician);
                    abpnClient.get<Examination>("/api/examination?examinationId=" + match.params.examinationId, authContext)
                        .then(exam => {
                            setExamination(exam);
                        });
                });
        });
    }, []);

    useEffect(() => {
        const getUseBC = async () => {
            let k = (await configService.getConfiguration()).FeatureFlags.UseBusinessCentral;
            if (typeof k === "string")
            {
                setUseBC(booleanify(k));
            }
            else {
                setUseBC(k);
            }

        }
        getUseBC();
    }, []);

    useEffect(() => {
        if (sectionNavigation.activeSections == null && application && examination && physician){
            let activeSections = [ApplicationSection.ApplicantInformation];
            activeSections.push(ApplicationSection.MedicalLicenseInformation);
            if (examination.isCombinedMoc) {
                activeSections.push(ApplicationSection.Modules);
            }
            if (examination.certificationId == KnownCertifications.ClinicalNeurophysiology && examination.isMoc && !examination.isArticleBased) {
                activeSections.push(ApplicationSection.CnpSection);
            }
            if (applicationRequiresTrainingSection) {
                activeSections.push(ApplicationSection.TrainingRequirements);
            }
            activeSections.push(ApplicationSection.AttestationStatement, ApplicationSection.TestingOptions, ApplicationSection.Payment);

            var updated = deepCopy(sectionNavigation);
            var goToPaySection = application?.candidateStatus !== CandidateStatus.Unsubmitted && application.order?.balance > 0;
            updated.navigateToPaySection = goToPaySection;
            updated.activeSections = activeSections;
            updated.sectionsComplete = goToPaySection ? activeSections.indexOf(ApplicationSection.Payment) : 0;

            setSectionNav(updated);
            setLoading(false);
        }
    }, [application, examination, physician])

    useEffect(() => {
        if (application && !attestationContent) {
            var applicationCreatedDate = getDateWithOffset(application.createdDate).toLocaleDateString("en-US");
            abpnClient.get<any>(`/api/applicationTemplate/attestation?examinationId=${match.params.examinationId}&applicationCreatedDate=${applicationCreatedDate}`, authContext)
                .then(template => {
                    setAttestationContent(template.attestationContent);
                });
        }
    }, [application]);

    const onApplicationAttestedToggled = () => {
        var updated = deepCopy(application);
        updated.attestationContent = attestationContent;
        updated.hasAttested = !application.hasAttested;
        setApplication(updated);
    }

    const onAttestationContinue = async () => {
        await saveApplication(application);
    }

    const onTestingOptionsContinue = async (isAdaRequested: boolean, isNmaRequested: boolean) => {
        var updated = deepCopy(application);
        updated.isAdaRequested = isAdaRequested;
        updated.isNmaRequested = isNmaRequested;
        await saveApplication(updated)
    }

    const onCertificationSelectionContinue = async (moduleCertificationKeys: Array<number>, cnpType: number) => {
        var updated = deepCopy(application)
        updated.modularCertificationIds = moduleCertificationKeys;
        updated.cnpType = cnpType;

        await saveApplication(updated)
    }

    const onCnpSectionContinue = async (cnpType: number) => {
        var updated = deepCopy(application)
        updated.cnpType = cnpType;

        await saveApplication(updated)
    }

    const onApplicationSubmitted = () => {
        setDisplayPaymentSuccessful(false);
        setIsSubmitted(true);
    }

    const onApplicationSubmittedWithInvoice = () => {
        setDisplayPaymentSuccessful(false);
        setIsSubmitted(true);
    }

    const updateSubSpecialtyTraining = (subSpecialtyTraining: Array<SubspecialtyTraining>) => {
        var updated = deepCopy(physician);
        updated.subspecialtyTrainingList = subSpecialtyTraining;
        setPhysician(updated);
        updateSectionsComplete(sectionNavigation.sectionsComplete + 1)
    }

    const requiresUnrestrictedLicense = () => {
        if (examination.examinationDefinitionId === KnownExaminationDefinitions.Neurology) return true;
        if (examination.examinationDefinitionId === KnownExaminationDefinitions.ChildNeurology) return true;
        if (examination.examinationDefinitionId === KnownExaminationDefinitions.Psychiatry) return true;
        return false;
    }

    const updateSectionsComplete = (index: number) => {
        var updatedNav = deepCopy(sectionNavigation);
        updatedNav.sectionsComplete = index;
        setSectionNav(updatedNav);
    }

    const saveAllAddresses = (addresses: Array<Address>) => {
        if (!addresses.find(_ => _.isContact).isValid) return;

        const url = "/api/Physician/SaveAddresses/";

        setLoading(true);
        abpnClient.post<Array<Address>>(url, addresses, authContext).then(result => {
            var updated = deepCopy(physician);
            var addressesToKeep = updated.addresses.addresses.filter(_ => !_.isContact);

            addressesToKeep.push(result.find(_ => _.isContact));
            physician.addresses.addresses = addressesToKeep;

            setPhysician(physician);
            setLoading(false);
            updateSectionsComplete(1);
        });
    }

    const history = useHistory();

    const displayBanner = () => {
        setDisplayPaymentSuccessful(true);
        setTimeout(() => { history.push(ApplicationPaths.DefaultLoginRedirectPath) }, 5000);
    }

    const isSectionDisabled = (section: ApplicationSection) => {
        return sectionNavigation.navigateToPaySection || (sectionNavigation.sectionsComplete < sectionNavigation.activeSections.indexOf(section));
    }

    const filteredExamCertificationIds = () => {
        const ccEnrollmentExamCertificates = physician.currentCCEnrollment?.activeEnrollmentCertificates?.filter(_ => _.ccAssessmentType == CCAssessmentType.Exam);
        var filteredCCEnrollmentCertificateIds = ccEnrollmentExamCertificates?.map(_ => _.certificateId);
        var filteredPhysicianCertificates = physician.certificates.filter(_ => filteredCCEnrollmentCertificateIds?.indexOf(_.certificateId) != -1);
        return filteredPhysicianCertificates?.map(_ => _.certificationId);
    }
        
    if (!(application && examination && physician && attestationContent && sectionNavigation.activeSections)) return <AbpnSpinner></AbpnSpinner>
    if (displayPaymentSuccessful) return <ActionResultMessage alertVariant="pp-alert-success" title="Payment Submitted" message='Payment Received Successfully.' subTitle={null} />
    if (isSubmitted) return <ApplicationReceived/>

    return ( 
        <div className="ml-3 mr-3" >
            <h3 className="mb-3 text-pp-secondary font-weight-bold">{examination.title}</h3>
            <h4 className="mb-3 text-pp-secondary font-weight-bold">{formattedApplicationDeadlineDate}
                <OverlayTrigger
                    placement={"right"}
                    overlay={<Tooltip id="applicationDeadline" bsPrefix="pp-tooltip">
                        Application deadlines are at 11:59 p.m. CST, after which the late fee will be applied. 
                    </Tooltip>}>
                    <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                </OverlayTrigger>
            </h4>
            <h4 className="mb-3 text-pp-secondary font-weight-bold">{examinationDates}</h4>
            <ApplicantInformation
                isNewPhysician={false}
                fullName={physician.fullName}
                addresses={physician.addresses.addresses}
                states={physician.addresses.states}
                countries={physician.addresses.countries}
                emailAddress={physician.emailAddress}
                isSectionOpen={previousSectionComplete(ApplicationSection.ApplicantInformation)}
                runValidation={false}
                onSave={(addresses: Array<Address>) => saveAllAddresses(addresses)}
                onOpen={() => updateSectionsComplete(0)}
                disabled={isSectionDisabled(ApplicationSection.ApplicantInformation)}
                isLoading={loading} />
            <MedicalLicenseInformation
                medicalLicenses={physician.medicalLicenses}
                examStartDate={examination.startDate}
                states={physician.addresses.states.filter(x => x.countryId == USCountryId)}
                isSectionOpen={previousSectionComplete(ApplicationSection.MedicalLicenseInformation)}
                requiresUnrestrictedLicense={requiresUnrestrictedLicense()}
                onContinue={() => { updateSectionsComplete(sectionNavigation.sectionsComplete + 1) }}
                onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.MedicalLicenseInformation))}
                disabled={isSectionDisabled(ApplicationSection.MedicalLicenseInformation)}
            />
            {
                examination.isCombinedMoc &&
                <Modules
                    isSectionOpen={previousSectionComplete(ApplicationSection.Modules)}
                    modules={examination.examinationModules}
                    moduleTypes={examination.moduleTypes}
                    certificates={physician.certificates}
                    examCertificationIds={filteredExamCertificationIds()}
                    onContinue={onCertificationSelectionContinue}
                    application={application}
                    onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.Modules))}
                    disabled={isSectionDisabled(ApplicationSection.Modules)}
                    isLoading={loading}
                />
            }
            {
                examination.certificationId == KnownCertifications.ClinicalNeurophysiology && examination.isMoc && !examination.isArticleBased &&
                <CnpSection
                    isSectionOpen={previousSectionComplete(ApplicationSection.CnpSection)}
                    certificationName={examination.certificationName}
                    cnpType={application.cnpType}
                    onContinue={onCnpSectionContinue}
                    onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.CnpSection))}
                    disabled={isSectionDisabled(ApplicationSection.CnpSection)}
                    isLoading={loading} />
            }
            {
                applicationRequiresTrainingSection &&
                <TrainingRequirements
                    isSectionOpen={previousSectionComplete(ApplicationSection.TrainingRequirements)}
                    onSave={(subSpecialtyTraining: Array<SubspecialtyTraining>) => updateSubSpecialtyTraining(subSpecialtyTraining)}
                    examination={examination}
                    applicationId={application.applicationId}
                    subSpecialtyTrainingList={physician.subspecialtyTrainingList}
                    onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.TrainingRequirements))}
                    disabled={isSectionDisabled(ApplicationSection.TrainingRequirements)}
                />
            }
            <AttestationStatement
                attestationContent={attestationContent}
                hasAttested={application.hasAttested}
                isSectionOpen={previousSectionComplete(ApplicationSection.AttestationStatement)}
                onApplicationAttestedToggled={onApplicationAttestedToggled}
                onContinue={onAttestationContinue}
                onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.AttestationStatement))}
                disabled={isSectionDisabled(ApplicationSection.AttestationStatement)}
                isLoading={loading}
            />
            <TestingOptions
                isAdaRequested={application.isAdaRequested}
                isNmaRequested={application.isNmaRequested}
                isArticleBased={examination.isArticleBased}
                isSectionOpen={previousSectionComplete(ApplicationSection.TestingOptions)}
                onContinue={onTestingOptionsContinue}
                onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.TestingOptions))}
                disabled={isSectionDisabled(ApplicationSection.TestingOptions)}
                isLoading={loading}
            />
            {  useBC ?
                <InvoicePaymentCard
                    physician={physician}
                    application={application}
                    examination={examination}
                    isSectionOpen={previousSectionComplete(ApplicationSection.Payment)}
                    isSectionAvailable={true}
                    onApplicationSubmitted={onApplicationSubmittedWithInvoice}
                    examinationDates={examinationDates}
                    onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.Payment))}
                    disabled={sectionNavigation.sectionsComplete < sectionNavigation.activeSections.indexOf(ApplicationSection.Payment)}
                    applicationDeadlineDate={applicationDeadlineDate}
                    totalBalance={application?.order?.balance ?? application?.totalFeeAmount}
                /> :
                <PaymentCard
                physician={physician}
                application={application}
                examination={examination}
                isSectionOpen={previousSectionComplete(ApplicationSection.Payment)}
                isSectionAvailable={true}
                onApplicationSubmitted={onApplicationSubmitted}
                examinationDates={examinationDates}
                displayBanner={displayBanner}
                onOpen={() => updateSectionsComplete(sectionNavigation.activeSections.indexOf(ApplicationSection.Payment))}
                disabled={sectionNavigation.sectionsComplete < sectionNavigation.activeSections.indexOf(ApplicationSection.Payment)}
                applicationDeadlineDate={applicationDeadlineDate}
                totalBalance={application?.order?.balance ?? application?.totalFeeAmount}
            />
            }
        </div>
    );
}

export default Application;